import { Link } from "@inertiajs/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import ContentLayout from "../shared/ContentLayout";
import PageHeader from "../shared/PageHeader";
import { MediaAttachmentCard } from "../Admin/AttachmentGroups/MediaAttachmentCard";

interface Attachment {
  id: number
  title: string | null
  description: string | null
  public_url: string
  thumbnail_url: string | null
  filename?: string
  created_at: string
}

interface AttachmentGroup {
  id: number
  title: string
  description: string | null
  attachments: Attachment[]
}

interface GroupInfo {
  id: number
  title: string
}

export default function MediaGroupShow({
  group,
  nextGroup,
  previousGroup,
}: {
  group: AttachmentGroup
  nextGroup: GroupInfo | null
  previousGroup: GroupInfo | null
}) {
  const renderBreadcrumbs = () => (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    }}>
      <div className="breadcrumbs text-sm">
        <ul>
          <li>
            <Link
              href="/"
              className="link link-primary"
            >
              Home
            </Link>
          </li>
          <li>
            <Link
              href="/media"
              className="link link-primary"
            >
              Media
            </Link>
          </li>
          <li>
            <span>{group.title}</span>
          </li>
        </ul>
      </div>

      <div style={{
        display: 'flex',
        gap: '8px',
      }}>
        {previousGroup && (
          <Link
            href={`/media/${previousGroup.id}`}
            className="btn btn-outline btn-primary btn-xs"
            title={previousGroup.title}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </Link>
        )}
        {nextGroup && (
          <Link
            href={`/media/${nextGroup.id}`}
            className="btn btn-outline btn-primary btn-xs"
            title={nextGroup.title}
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </Link>
        )}
      </div>
    </div>
  );

  const renderFooterNavigation = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div>
        {previousGroup && (
          <Link
            href={`/media/${previousGroup.id}`}
            className="btn btn-outline btn-primary w-full justify-start"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
            }}>
              <div className="truncate">{previousGroup.title}</div>
            </div>
          </Link>
        )}
      </div>

      <div className="flex items-center justify-center">
        <Link
          href="/media"
          className="btn btn-primary btn-sm"
        >
          View All Media
        </Link>
      </div>

      <div>
        {nextGroup && (
          <Link
            href={`/media/${nextGroup.id}`}
            className="btn btn-outline btn-primary w-full justify-end"
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
            }}>
              <div className="truncate">{nextGroup.title}</div>
            </div>
            <FontAwesomeIcon icon={faChevronRight} />
          </Link>
        )}
      </div>
    </div>
  );

  return (
    <ContentLayout
      breadcrumbs={renderBreadcrumbs()}
      footerNavigation={renderFooterNavigation()}
    >
      <div className="p-3 sm:p-4 max-w-4xl">
        <PageHeader title={group.title} description={group.description || undefined} />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {group.attachments.map((attachment) => (
            <MediaAttachmentCard
              key={`attachment-card-${attachment.id}`}
              attachment={attachment}
              href={`/media/${group.id}/attachments/${attachment.id}`}
            />
          ))}
        </div>
      </div>
    </ContentLayout>
  );
}
